import get from 'lodash/get';
import forEach from 'lodash/forEach';
import keyBy from 'lodash/keyBy';

import { caseLogicMsg } from '../validationMessages';
import getInputActionNodes from './getInputActionNodes';
import getAvailableScopes from '~lib/common/utils/getAvailableScopes';

// The actions, checks, and effects all have these types
const customStateTypes = [
  'CUSTOM_STATE',
  'CUSTOM_ENUM_STATE',
];

const customStateTitleByType = {
  CUSTOM_STATE: 'Custom state',
  CUSTOM_ENUM_STATE: 'Custom enum state',
};

function validateCustomStateNodes(caseConf, nodeInfo) {
  const errors = [];
  const warnings = [];

  const customStatesById = keyBy(get(caseConf, 'caseLogicDescription.customStates'), 'id');
  const customEnumStatesById = keyBy(get(caseConf, 'caseLogicDescription.customEnumStates'), 'id');
  const statesByTypeById = {
    CUSTOM_STATE: customStatesById,
    CUSTOM_ENUM_STATE: customEnumStatesById,
  };

  const availableScopes = getAvailableScopes(caseConf);

  // Construct a map of each custom (enum) state event to a list of custom (enum)
  // state effects that change the value of the event
  const eventToEffectList = {};

  nodeInfo.actionNodes.forEach(node => {
    const action = get(node, 'data.action');
    if (customStateTypes.includes(action.type)) {
      const stateItem = statesByTypeById[action.type][action.id];
      const stateTitle = customStateTitleByType[action.type];
      if (!action.id) {
        errors.push(
          caseLogicMsg(`${stateTitle} is mandatory`, node.id)
        );
      } else if (!stateItem) {
        errors.push(
          caseLogicMsg(`${stateTitle} no longer exists, this node must be removed`, node.id)
        );
      } else if (action.value === undefined || action.value === null) {
        errors.push(
          caseLogicMsg(`${stateTitle} value is mandatory`, node.id)
        );
      } else if (stateItem.values && !stateItem.values.find(v => v.id === action.value)) {
        errors.push(
          caseLogicMsg('Value no longer exists', node.id)
        );
      } else if (!availableScopes.find(scope => scope.id === stateItem.scope)) {
        errors.push(
          caseLogicMsg('Select valid scope: Scenario, Patient, or NPC', node.id)
        );
      }
    }
  });

  nodeInfo.effectNodes.forEach(node => {
    const effect = get(node, 'data.effect');
    if (customStateTypes.includes(effect.type)) {
      // Look at the effect for issues
      const stateItem = statesByTypeById[effect.type][effect.id];
      const stateTitle = customStateTitleByType[effect.type];
      if (!effect.id) {
        errors.push(
          caseLogicMsg(`${stateTitle} is mandatory`, node.id)
        );
      } else if (!stateItem) {
        errors.push(
          caseLogicMsg(`${stateTitle} no longer exists, this node must be removed`, node.id)
        );
      } else if (effect.value === undefined || effect.value === null) {
        errors.push(
          caseLogicMsg(`${stateTitle} value is mandatory`, node.id)
        );
      } else if (stateItem.values && !stateItem.values.find(v => v.id === effect.value)) {
        errors.push(
          caseLogicMsg('Value no longer exists', node.id)
        );
      } else if (!availableScopes.find(scope => scope.id === stateItem.scope)) {
        errors.push(
          caseLogicMsg('Select valid scope: Scenario, Patient, or NPC', node.id)
        );
      }

      // Add to the eventToEffectList for later checking
      const actionNodes = getInputActionNodes(node, nodeInfo.nodeMap);
      for (const actionNode of actionNodes) {
        const action = get(actionNode, 'data.action');
        // We only care about custom (enum) state events that are operating on the
        // same custom (enum) state as this effect
        if (customStateTypes.includes(action.type) && action.id === effect.id) {
          let mapping = eventToEffectList[actionNode.id];
          if (!mapping) {
            eventToEffectList[actionNode.id] = [];
            mapping = eventToEffectList[actionNode.id];
          }
          mapping.push(effect);
        }
      }

    }
  });

  nodeInfo.checkNodes.forEach(node => {
    forEach(node.data, (check, checkKey) => {
      if (customStateTypes.includes(check.type)) {
        const stateItem = statesByTypeById[check.type][check.id];
        const stateTitle = customStateTitleByType[check.type];
        if (!check.id) {
          errors.push(
            caseLogicMsg(`${stateTitle} is mandatory`, node.id, { checkKey })
          );
        } else if (!stateItem) {
          errors.push(
            caseLogicMsg(`${stateTitle} no longer exists, this check must be removed`, node.id, { checkKey })
          );
        } else if (check.value === undefined || check.value === null) {
          errors.push(
            caseLogicMsg(`${stateTitle} value is mandatory`, node.id)
          );
        } else if (stateItem.values && !stateItem.values.find(v => v.id === check.value)) {
          errors.push(
            caseLogicMsg('Value no longer exists', node.id)
          );
        }  else if (!availableScopes.find(scope => scope.id === stateItem.scope)) {
          errors.push(
            caseLogicMsg('Select valid scope: Scenario, Patient, or NPC', node.id, { checkKey })
          );
        }
      }
    });
  });

  // Look for infinite loops
  for (const [actionId, effectList] of Object.entries(eventToEffectList)) {
    if (incompatibleEffects(effectList, customEnumStatesById)) {
      const type = effectList[0].type;
      let message;
      if (type === 'CUSTOM_ENUM_STATE') {
        message = 'Downstream effects cannot set the custom enum state to all possible values'
      } else {
        message = 'Downstream effects cannot set the custom state to both true and false';
      }
      errors.push(caseLogicMsg(message, actionId));
    }
  }

  return {
    errors,
    warnings,
  };
}

/**
 *
 * @param {*} effectList
 * @returns true if the effects are inocompatible, potentially causing an infinte loop
 *   Example: two custom state effects that set the value to true and false
 *   Example: custom enum state effects that set the value to all possible values
 */
function incompatibleEffects(effectList, customEnumStatesById) {
  if (effectList.length > 0) {
    // Get the type from the first effect (they should all have the same type)
    const type = effectList[0].type;
    const id = effectList[0].id;
    if (type === 'CUSTOM_STATE') {
      const hasTrue = effectList.some(obj => obj.value === true);
      const hasFalse = effectList.some(obj => obj.value === false);
      return (hasTrue && hasFalse);
    }
    if (type === 'CUSTOM_ENUM_STATE') {
      const customEnumState = customEnumStatesById[id] || { values: [] };
      const hasAllValues = customEnumState.values.every(v =>
        effectList.some(effect => effect.value === v.id)
      );
      return hasAllValues;
    }
  }
  return false;
}

export default validateCustomStateNodes;
